import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import downloadFile from '@framework/api/downloadFile';
import { getContainer } from '@framework/di/getContainer';
import DownloadRepository from '@/creditflowresearch/infrastructure/repository/DownloadRepository';
import { useCorporateBondScreenerContext } from '@/creditflowresearch/application/CorporateBondScreener/CorporateBondScreenerContext';
import { useCorporateBondScreenerMySelectionContext } from '@/creditflowresearch/application/CorporateBondScreener/CorporateBondScreenerMySelectionContext';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import type {
  ICorporateBondScreenerExportAllColumns,
  ICorporateBondScreenerExportPayload,
  ICorporateBondScreenerExportSelectedColumns,
} from '@/creditflowresearch/domain/vo/Bondscreener/ICorporateBondScreenerExportPayload';
import type { TCorporateBondScreenerSortBy } from '@/creditflowresearch/domain/vo/Bondscreener/ICorporateBondScreenerRequest';
import {
  adaptFiltersToRequestFormat,
  restrictToAccessibleMarkets,
} from '@/creditflowresearch/application/CorporateBondScreener/buildCorporateBondScreenerRequest';
import {
  useRestrictedAccess,
} from '@/creditflowresearch/application/User/access/grants/useRestrictedAccess';
import createUseExportProgress from '@/creditflowresearch/application/BondScreener/createUseExportProgress';
import {
  useBondScreenerColumnsContext,
} from '@/creditflowresearch/application/BondScreener/BondScreenerColumnsContext';

const useExportBondScreener = createUseExportProgress({
  errorLabel: 'Corporate bond screener export failed',
});

const useExportCorporateBondScreener = () => {
  const container = getContainer();
  const { filterPayload, sortBy, sortOrder, debouncedSearchString } = useCorporateBondScreenerContext();
  const { selectedIds } = useCorporateBondScreenerMySelectionContext();
  const { activeColumnsIds } = useBondScreenerColumnsContext();
  const { run, ...exportState } = useExportBondScreener();
  const { regions: accessibleRegionIds } = useUserPlanContext();
  // TODO: remove when the European market opening period ends
  const isRestrictedToEuropeanMarket = useRestrictedAccess();

  const buildBasePayload = useCallback((): Omit<ICorporateBondScreenerExportPayload, 'mode' | 'columns'> => ({
    exportId: uuid(),
    filters: adaptFiltersToRequestFormat(
      restrictToAccessibleMarkets(filterPayload, accessibleRegionIds, isRestrictedToEuropeanMarket),
    ),
    search: debouncedSearchString || null,
    sorting: {
      sortBy: sortBy as TCorporateBondScreenerSortBy,
      sortOrder: sortOrder as 'asc' | 'desc',
    },
    selections: [...selectedIds],
  }), [
    filterPayload,
    accessibleRegionIds,
    isRestrictedToEuropeanMarket,
    debouncedSearchString,
    sortBy,
    sortOrder,
    selectedIds,
  ]);

  const onExportAll = useCallback((): void => {
    const payload: ICorporateBondScreenerExportAllColumns = {
      ...buildBasePayload(),
      mode: 'all',
    };

    run(async (onProgress) => {
      const repository = container.get<DownloadRepository>(DownloadRepository);
      const url = await repository.exportCorporateBondScreenerExcel(payload, onProgress);
      downloadFile(url);
    });
  }, [run, container, buildBasePayload]);

  const onExportSelected = useCallback((): void => {
    const payload: ICorporateBondScreenerExportSelectedColumns = {
      ...buildBasePayload(),
      mode: 'selected',
      columns: [...activeColumnsIds],
    };

    run(async (onProgress) => {
      const repository = container.get<DownloadRepository>(DownloadRepository);
      const url = await repository.exportCorporateBondScreenerExcel(payload, onProgress);
      downloadFile(url);
    });
  }, [run, container, buildBasePayload, activeColumnsIds]);

  return { ...exportState, onExportAll, onExportSelected };
};

export default useExportCorporateBondScreener;
